import {
  useAllPiiBackfillStates,
  usePauseShopsPiiBackfill,
  useStartShopsPiiBackfill,
} from "@/api/pii-vault/use-pii-backfill";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { Pause, Play } from "lucide-react";
import { useMemo, useState } from "react";
import type { BackfillShop } from "./pii-backfill-shop-row";
import { PiiEmailRefRow } from "./pii-email-ref-row";
import {
  EMAIL_REF_STATUSES,
  type EmailRefStatus,
  summarizeEmailRef,
} from "./pii-email-ref-status";

/** The API bounds one start request: each shop counts its documents first. */
const MAX_SHOPS_PER_START = 100;

/**
 * Super-admin launcher of the emailRef migration: stamps, on every document
 * the backfill recorded, which of the customer's emails it was recorded with.
 * Only `piiRefs.emailRef` is written, from the plaintext email still on the
 * document, so the migration must run before plaintext is dropped.
 */
export function PiiEmailRefTab({ shops }: { shops: BackfillShop[] }) {
  const overview = useAllPiiBackfillStates();
  const start = useStartShopsPiiBackfill();
  const pause = usePauseShopsPiiBackfill();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const rows = useMemo(() => {
    const states = overview.data?.states ?? [];
    return [...shops]
      .sort((a, b) => a.totalOrders - b.totalOrders)
      .map((shop) => ({
        shop,
        summary: summarizeEmailRef(states.filter((s) => s.shopId === shop.id)),
      }));
  }, [shops, overview.data]);

  const counts = rows.reduce<Partial<Record<EmailRefStatus, number>>>(
    (acc, { summary }) => ({
      ...acc,
      [summary.status]: (acc[summary.status] ?? 0) + 1,
    }),
    {},
  );
  const shopIds = [...selected];
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectPending = () =>
    setSelected(
      new Set(
        rows
          .filter(({ summary }) => summary.status === "pending")
          .slice(0, MAX_SHOPS_PER_START)
          .map(({ shop }) => shop.id),
      ),
    );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Ajoute à chaque document déjà rangé dans le coffre la référence de
        l'email avec lequel il a été enregistré. Sans elle, le coffre rend le
        dernier email du client. À terminer avant de supprimer les données en
        clair : la référence est calculée depuis l'email en clair du document.
        Un shop dont le backfill n'est pas terminé reçoit la référence par son
        backfill.
      </p>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(EMAIL_REF_STATUSES) as EmailRefStatus[]).map((status) => (
          <Badge key={status} variant="outline">
            {EMAIL_REF_STATUSES[status]} : {counts[status] ?? 0}
          </Badge>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-3">
        <Button size="sm" variant="outline" onClick={selectPending}>
          Sélectionner les shops à migrer (max {MAX_SHOPS_PER_START})
        </Button>
        <Button
          size="sm"
          disabled={shopIds.length === 0 || start.isPending}
          onClick={() => start.mutate({ body: { shopIds, mode: "emailRef" } })}
        >
          <Play className="w-4 h-4" />
          Migrer {shopIds.length} shop(s)
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={shopIds.length === 0 || pause.isPending}
          onClick={() => pause.mutate({ body: { shopIds } })}
        >
          <Pause className="w-4 h-4" />
          Pause
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-background">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2" />
              <th className="px-3 py-2">Shop</th>
              <th className="px-3 py-2 text-right">Commandes</th>
              <th className="px-3 py-2">Migration</th>
              <th className="px-3 py-2">Progression</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ shop, summary }) => (
              <PiiEmailRefRow
                key={shop.id}
                shop={shop}
                summary={summary}
                isSelected={selected.has(shop.id)}
                onToggle={() => toggle(shop.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
